import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import ConsultationDialog from './ConsultationDialog';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isConsultationOpen, setIsConsultationOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  const services = [
    { name: 'Corporate & Business Contracts', slug: 'corporate-business-contracts' },
    { name: 'Corporate & Business Formation', slug: 'corporate-business-formation' },
    { name: 'Business Transaction', slug: 'business-transaction' },
    { name: 'Mergers & Acquisitions', slug: 'mergers-acquisitions' },
    { name: 'Non-Profit Law', slug: 'non-profit-law' },
    { name: 'Government Contracting', slug: 'government-contracting' },
    { name: 'Business Disputes', slug: 'business-disputes' },
    { name: 'Anti-Corruption Compliance', slug: 'anti-corruption-compliance' },
    { name: 'Immigration', slug: 'immigration' },
    { name: 'Trademark Registration', slug: 'trademark-registration' },
    { name: 'Copyright Registration', slug: 'copyright-registration' },
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
        }`}
        data-testid="main-header"
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
              <img 
                src="/images/logo-navbar.png" 
                alt="Zorvian Associates" 
                className="h-14 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              <Link
                to="/about-us"
                className="text-[#0F172A] hover:text-[#2563EB] font-medium transition-colors"
                data-testid="about-link"
              >
                About Us
              </Link>
              <Link
                to="/our-attorneys"
                className="text-[#0F172A] hover:text-[#2563EB] font-medium transition-colors"
                data-testid="attorneys-link"
              >
                Our Attorneys
              </Link>
              
              {/* Services Mega Menu */}
              <div
                className="relative group"
                onMouseEnter={() => setIsServicesOpen(true)}
                onMouseLeave={() => setIsServicesOpen(false)}
              >
                <button
                  className="flex items-center space-x-1 text-[#0F172A] hover:text-[#2563EB] font-medium transition-colors"
                  data-testid="services-dropdown-trigger"
                >
                  <span>Services</span>
                  <ChevronDown className="h-4 w-4" />
                </button>
                
                <div className={`absolute left-1/2 -translate-x-1/2 mt-2 w-[600px] transition-all duration-200 ${isServicesOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}>
                  <div className="bg-white rounded-lg shadow-2xl border border-slate-200 p-6 max-h-[500px] overflow-y-auto">
                    <div className="grid grid-cols-2 gap-3">
                      {services.map((service) => (
                        <Link
                          key={service.slug}
                          to={`/services/${service.slug}`}
                          className="px-4 py-3 rounded-lg hover:bg-slate-50 transition-colors text-[#0F172A] hover:text-[#2563EB] font-medium"
                          data-testid={`service-link-${service.slug}`}
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <Link
                to="/contact-us"
                className="text-[#0F172A] hover:text-[#2563EB] font-medium transition-colors"
                data-testid="contact-link"
              >
                Contact Us
              </Link>
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <Button
                onClick={() => setIsConsultationOpen(true)}
                className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-6 py-2 rounded-lg font-semibold btn-primary"
                data-testid="get-legal-help-button"
              >
                Get Legal Help
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-[#0F172A]"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <div className="lg:hidden py-4 border-t border-slate-200" data-testid="mobile-menu">
              <nav className="flex flex-col space-y-4">
                <Link
                  to="/about-us"
                  className="text-[#0F172A] hover:text-[#2563EB] font-medium px-4 py-2"
                >
                  About Us
                </Link>
                <Link
                  to="/our-attorneys"
                  className="text-[#0F172A] hover:text-[#2563EB] font-medium px-4 py-2"
                >
                  Our Attorneys
                </Link>
                
                {/* Mobile Services Accordion */}
                <div>
                  <button
                    onClick={() => setIsServicesOpen(!isServicesOpen)}
                    className="flex items-center justify-between w-full text-[#0F172A] font-medium px-4 py-2"
                  >
                    <span>Services</span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {isServicesOpen && (
                    <div className="pl-8 pr-4 py-2 space-y-2">
                      {services.map((service) => (
                        <Link
                          key={service.slug}
                          to={`/services/${service.slug}`}
                          className="block text-sm text-slate-600 hover:text-[#2563EB] py-1"
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                <Link
                  to="/contact-us"
                  className="text-[#0F172A] hover:text-[#2563EB] font-medium px-4 py-2"
                >
                  Contact Us
                </Link>
                
                <div className="px-4 pt-2">
                  <Button
                    onClick={() => setIsConsultationOpen(true)}
                    className="w-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white py-2 rounded-lg font-semibold"
                  >
                    Get Legal Help
                  </Button>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      <ConsultationDialog
        open={isConsultationOpen}
        onOpenChange={setIsConsultationOpen}
      />
    </>
  );
};

export default Header;
